import type { JSX } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useTranslation } from 'react-i18next';
import { computeRoleDistribution } from '../types.js';
import './PrintedCast.css';

interface PrintedCastProps {
  playerCount: number;
  foolEnabled: boolean;
  /** Below this count, the cast slot becomes a printed waiting line instead of counts. */
  minPlayers: number;
}

/**
 * Recto's printed cast line. Reads as a programme-card entry beneath the
 * village illustration:
 *
 *     Tonight's Cast
 *     4 Villagers · 1 Ghost · 1 Fool
 *
 * Each role carries a chapbook-footnote tooltip — a 1px dotted underline
 * on the role label signals further explanation, hover/tap surfaces the
 * role's mechanics and win condition. Tooltip copy lives in
 * lobby.roles.descriptions.* (en.json).
 *
 * Below the minimum player count the slot keeps the same italic-printed-
 * card register but swaps the counts for the same waiting copy used
 * elsewhere in the lobby (lobby.empty.solo / lobby.empty.gathering), so
 * the recto stays in conversation with the verso's Company grid (an
 * empty chair appears as the cast line shortens to a waiting line).
 */
export function PrintedCast({
  playerCount,
  foolEnabled,
  minPlayers,
}: PrintedCastProps): JSX.Element {
  const { t } = useTranslation();
  const isBelowMin = playerCount < minPlayers;

  if (isBelowMin) {
    const remaining = Math.max(0, minPlayers - playerCount);
    const waitingText =
      playerCount <= 1
        ? t('lobby.empty.solo')
        : t('lobby.empty.gathering', { count: remaining });
    return (
      <div className="susdle-cast" data-mode="waiting" aria-live="polite">
        <p className="susdle-cast__label">{t('lobby.sections.cast')}</p>
        <p className="susdle-cast__waiting">{waitingText}</p>
      </div>
    );
  }

  const { villagers, ghosts, fools } = computeRoleDistribution(playerCount, foolEnabled);
  const foolUnproduced = foolEnabled && fools === 0;

  return (
    <Tooltip.Provider delayDuration={200} skipDelayDuration={500}>
      <div className="susdle-cast" data-mode="counts" aria-live="polite">
        <p className="susdle-cast__label">{t('lobby.sections.cast')}</p>
        <p className="susdle-cast__counts">
          <CastCount
            n={villagers}
            label={t('lobby.roles.villager', { count: villagers })}
            tooltip={t('lobby.roles.descriptions.villagers')}
          />
          <CastSeparator />
          <CastCount
            n={ghosts}
            label={t('lobby.roles.ghost', { count: ghosts })}
            tooltip={t('lobby.roles.descriptions.ghosts')}
          />
          {foolEnabled ? (
            <>
              <CastSeparator />
              <CastCount
                n={fools}
                label={t('lobby.roles.fool', { count: fools })}
                faded={foolUnproduced}
                tooltip={
                  foolUnproduced
                    ? t('lobby.roles.foolNotProduced')
                    : t('lobby.roles.descriptions.fool')
                }
              />
            </>
          ) : null}
        </p>
      </div>
    </Tooltip.Provider>
  );
}

function CastCount({
  n,
  label,
  tooltip,
  faded = false,
}: {
  n: number;
  label: string;
  tooltip: string;
  faded?: boolean;
}): JSX.Element {
  const entry = (
    <span
      className="susdle-cast__entry"
      data-faded={faded ? 'yes' : 'no'}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- the <span> is a Radix Tooltip.Trigger (asChild); it must be focusable so keyboard users can open the tooltip.
      tabIndex={0}
    >
      <span className="susdle-cast__n">{n}</span>
      <span className="susdle-cast__role">{label}</span>
    </span>
  );
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{entry}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content className="susdle-tooltip" sideOffset={6}>
          {tooltip}
          <Tooltip.Arrow className="susdle-tooltip__arrow" width={10} height={5} />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

function CastSeparator(): JSX.Element {
  return (
    <span aria-hidden className="susdle-cast__sep">
      ·
    </span>
  );
}
